import {
  capitalize,
  styled,
  ButtonGroup as MuiButtonGroup, // eslint-disable-line @typescript-eslint/no-restricted-imports
  buttonGroupClasses,
} from '@mui/material';
import { buttonColorPaletteStylesCache } from 'src/wsd/components/Button/style/palette';
import { getButtonSizeStyleValues } from 'src/components/Button/buttonSizeUtils';
import { getLineHeightForComponentSize } from 'src/utils/componentSizes';
import buttonClasses from 'src/wsd/components/Button/buttonClasses';
import type { ButtonGroupOwnProps } from 'src/components/ButtonGroup/ButtonGroup';
import wkTheme from 'src/wsd/styles/theme/wkTheme';
import { colorChannel } from '@workiva/unify-tokens';

/**
 * A styled MuiButtonGroup made to work with `WsdButtonRoot` styled children to compose a legacy "WSD" ButtonGroup.
 *
 * Rendered by our custom ButtonGroup component.
 */
export const WsdButtonGroupRoot = styled(MuiButtonGroup, {
  name: 'WsdButtonGroup',
  slot: 'Root',
})<ButtonGroupOwnProps>(function WsdButtonGroupRoot(inProps) {
  // Use wkTheme directly to get old theme values that are no longer
  // available from themeContext since ThemeProvider will soon stop
  // rendering a MUI ThemeProvider under the hood. See: FED-3568
  // eslint-disable-next-line deprecation/deprecation
  const theme = wkTheme;

  const defaultProps = theme.components?.MuiButtonGroup?.defaultProps;
  const {
    color = defaultProps?.color ?? 'primary',
    orientation = defaultProps?.orientation ?? 'horizontal',
    size = defaultProps?.size ?? 'medium',
    variant = defaultProps?.variant ?? 'text',
    disableElevation = defaultProps?.disableElevation ?? false,
  } = inProps;

  const colors = buttonColorPaletteStylesCache(theme.palette, true)[color][
    variant
  ];

  const textButtonDividerColor = buttonColorPaletteStylesCache(
    theme.palette,
    true
  )[color]['contained']?.bdColors.default;

  const nestedButtonColorClassName = `MuiButton-contained${capitalize(color)}`;
  const buttonSizeStyleValues = getButtonSizeStyleValues(true)[size];

  return {
    borderRadius:
      theme.shape.borderRadius * buttonSizeStyleValues.borderRadiusMultiplier,
    fontSize: buttonSizeStyleValues.fontSizeInPx,
    lineHeight: getLineHeightForComponentSize(size),

    // Use descendant instead of child selector so that this works for buttons with wrapper spans,
    // needed for tooltips on disabled buttons.
    [`& .${buttonGroupClasses.grouped}`]: {
      minWidth: 0,
      ...(variant === 'contained' && {
        ...(orientation === 'horizontal' && {
          // WSD contained buttons have a border applied to all sides by default so
          // this style is necessary to avoid double-border between adjacent buttons.
          '&:not(:first-of-type)': {
            marginLeft: -1, // This is how WSD removes the double border
          },
        }),
        ...(orientation === 'vertical' && {
          ['&:active, &[aria-selected="true"], &[aria-expanded="true"]']: {
            // Remove "punch" effect
            top: '0',
          },

          // WSD contained buttons have a border applied to all sides by default so
          // this style is necessary to avoid double-border between adjacent buttons.
          '&:not(:first-of-type)': {
            marginTop: -1, // This is how WSD removes the double border
          },
        }),
      }),

      ...(variant === 'text' && {
        ...(textButtonDividerColor && {
          [`&.${buttonGroupClasses.firstButton}, &.${buttonGroupClasses.middleButton}`]:
            {
              borderColor: `rgba(${colorChannel(
                textButtonDividerColor
              )} / 0.5)`,
            },
        }),

        ...(orientation === 'horizontal' && {
          // WSD's custom button styles add 1px of transparent border to
          // all buttons - even the text variant, so the default MUI
          // group styles to apply border colors results in borders
          // appearing around text buttons instead of just between them.
          //
          // The !important is to apply this rule to all text variants -
          // regardless of the color value.
          borderTopColor: 'transparent !important',
          borderBottomColor: 'transparent !important',
          borderLeftColor: 'transparent !important',

          // If its a "VANILLA" button skin, don't put the border between adjacent buttons at all.
          // Double class specificity necessary to override:
          //   1. MUI's :not(:last-of-type) qualifier
          //   2. MUI's `MuiButtonGroup-middleButton` class
          // TODO: https://jira.atl.workiva.net/browse/FED-2612
          // eslint-disable-next-line
          [`&.${buttonClasses.textInherit}.${buttonClasses.textInherit}`]: {
            borderRightColor: 'transparent',
          },
        }),
        ...(orientation === 'vertical' && {
          // WSD's custom button styles add 1px of transparent border to
          // all buttons - even the text variant, so the default MUI
          // group styles to apply border colors results in borders
          // appearing around text buttons instead of just between them.
          //
          // The !important is to apply this rule to all text variants -
          // regardless of the color value.
          borderTopColor: 'transparent !important',
          borderRightColor: 'transparent !important',
          borderLeftColor: 'transparent !important',
        }),
      }),

      [`&.${buttonClasses.noText}`]: {
        width: buttonSizeStyleValues.minHeightInPx,
      },
    },

    ...(variant === 'contained' && {
      ...(orientation === 'horizontal' && {
        // The box-shadow is applied to the group node since the MUI
        // ButtonGroup component sets `box-shadow: none` on child buttons
        // and we don't want to re-declare the `boxShadow` styles using
        // the child button selector.
        //
        // This also aligns with how MUI handles the "elevation" of buttons
        // within button groups.
        boxShadow: disableElevation
          ? 'none'
          : colors?.boxShadows?.default ?? 'none',
      }),

      // Use descendant instead of child selector so that this works for buttons with wrapper spans,
      // needed for tooltips on disabled buttons.
      // Double class specificity necessary to override MUI's :not(:last-of-type) qualifier
      [`& .${nestedButtonColorClassName}.${nestedButtonColorClassName}`]: {
        borderColor: colors?.bdColors.default ?? undefined,

        '&:hover': {
          borderColor: colors?.bdColors.hover ?? undefined,
        },

        ['&:active, &[aria-selected="true"], &[aria-expanded="true"]']: {
          borderColor:
            colors?.bdColors.press ?? colors?.bdColors.hover ?? undefined,
        },

        [`&.${buttonClasses.focusVisible}`]: {
          borderColor:
            colors?.bdColors.focus ?? colors?.bdColors.hover ?? undefined,
        },
      },
    }),
    ...(variant === 'contained' &&
      orientation === 'vertical' && {
        boxShadow: 'none',
      }),
  };
});

export default WsdButtonGroupRoot;
